import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, TrendingUp, TrendingDown, Loader2, Trophy } from "lucide-react";

interface QuarterlyRow {
  id: string;
  protocol_slug: string;
  protocol_name: string;
  category: string | null;
  logo_url: string | null;
  symbol: string | null;
  quarter: string;
  quarter_start: string;
  quarter_end: string;
  revenue_usd: number;
  revenue_aud: number;
  annualized_revenue_aud: number | null;
  market_cap_aud: number | null;
  price_to_fees_ratio: number | null;
  rank: number;
  qoq_change_pct: number | null;
  available_on_swyftx: boolean;
  available_on_coinspot: boolean;
  defillama_url: string | null;
}

const SWYFTX_AFF = "https://trade.swyftx.com.au/register/?ref=cryptotipsau";
const COINSPOT_AFF = "https://www.coinspot.com.au/join/REFWV4YMH";

function fmtAud(v: number | null | undefined) {
  if (!v && v !== 0) return "—";
  if (v >= 1_000_000_000) return `A$${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `A$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `A$${(v / 1_000).toFixed(1)}K`;
  return `A$${v.toFixed(2)}`;
}

function quarterLabel(key: string) {
  const [y, q] = key.split("-");
  return `${q} ${y}`;
}

export default function QuarterlyRevenueLeaderboard() {
  const [rows, setRows] = useState<QuarterlyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeQ, setActiveQ] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("protocol_revenue_quarterly")
        .select("*")
        .order("quarter_start", { ascending: false })
        .order("rank", { ascending: true });
      if (!error && data) setRows(data as QuarterlyRow[]);
      setLoading(false);
    })();
  }, []);

  const quarters = useMemo(() => {
    const seen = new Map<string, string>();
    rows.forEach((r) => seen.set(r.quarter, r.quarter_start));
    return Array.from(seen.entries())
      .sort((a, b) => (a[1] < b[1] ? 1 : -1))
      .map(([k]) => k)
      .slice(0, 4);
  }, [rows]);

  useEffect(() => {
    if (!activeQ && quarters.length) setActiveQ(quarters[0]);
  }, [quarters, activeQ]);

  const byQuarter = useMemo(() => {
    const m: Record<string, QuarterlyRow[]> = {};
    rows.forEach((r) => {
      (m[r.quarter] ||= []).push(r);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.rank - b.rank));
    return m;
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Top Revenue Protocols — Quarterly
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Top 10 revenue-generating crypto protocols, last 4 quarters. Updated quarterly. Source: DefiLlama.
            </p>
          </div>
          <Badge variant="outline">AUD</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading leaderboard…
          </div>
        ) : quarters.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No quarterly data yet. The collector will populate it shortly.
          </div>
        ) : (
          <Tabs value={activeQ ?? quarters[0]} onValueChange={setActiveQ}>
            <TabsList className="grid grid-cols-4 w-full max-w-xl">
              {quarters.map((q) => (
                <TabsTrigger key={q} value={q}>{quarterLabel(q)}</TabsTrigger>
              ))}
            </TabsList>
            {quarters.map((q) => (
              <TabsContent key={q} value={q} className="mt-4">
                <div className="space-y-2">
                  {(byQuarter[q] || []).map((r) => {
                    const pos = (r.qoq_change_pct ?? 0) >= 0;
                    const buyHref = r.available_on_swyftx ? SWYFTX_AFF : r.available_on_coinspot ? COINSPOT_AFF : null;
                    const buyLabel = r.available_on_swyftx ? "Buy on Swyftx" : r.available_on_coinspot ? "Buy on CoinSpot" : null;
                    return (
                      <div
                        key={r.id}
                        className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/40 transition-colors"
                      >
                        <div className="text-lg font-bold w-8 text-center text-muted-foreground">#{r.rank}</div>
                        {r.logo_url ? (
                          <img src={r.logo_url} alt={r.protocol_name} className="h-8 w-8 rounded-full bg-muted" loading="lazy" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-muted" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">
                            {r.protocol_name}
                            {r.symbol ? <span className="text-muted-foreground font-normal ml-2">{r.symbol}</span> : null}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                            {r.category && <Badge variant="secondary" className="text-[10px]">{r.category}</Badge>}
                            {r.price_to_fees_ratio ? <span>P/F {r.price_to_fees_ratio.toFixed(1)}x</span> : null}
                            {r.market_cap_aud ? <span>MCap {fmtAud(r.market_cap_aud)}</span> : null}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">{fmtAud(r.revenue_aud)}</div>
                          <div className="text-xs text-muted-foreground">
                            Annualised {fmtAud(r.annualized_revenue_aud)}
                          </div>
                          {r.qoq_change_pct !== null && r.qoq_change_pct !== undefined && (
                            <div className={`text-xs flex items-center justify-end gap-1 ${pos ? "text-green-500" : "text-red-500"}`}>
                              {pos ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                              {pos ? "+" : ""}{r.qoq_change_pct.toFixed(1)}% QoQ
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 ml-2">
                          {buyHref && (
                            <Button asChild size="sm" className="h-7 text-xs">
                              <a href={buyHref} target="_blank" rel="noopener noreferrer">{buyLabel}</a>
                            </Button>
                          )}
                          {r.defillama_url && (
                            <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
                              <a href={r.defillama_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-3 w-3 mr-1" /> DefiLlama
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}
